/**
 * Persist captured email/phone hashes in localStorage so they survive
 * page navigations (e.g. form page → thank-you page).
 *
 * Only SHA-256 hashes are stored — never raw PII.
 * TTL: 30 minutes (covers form submit → redirect → conversion page).
 */

import { state, debugLog } from "./state.js";
import { isMarketingAllowed } from "./consent.js";

const STORAGE_KEY = "_tst_ud";
const TTL_MS = 30 * 60 * 1000; // 30 minutes

/**
 * Check if localStorage persistence is allowed.
 * Default (no consent config): always allowed — we only restrict when
 * the site has explicitly configured consent management AND the user denied.
 */
function isStorageAllowed(): boolean {
  if (!state.config?.consent?.noCookieOnDenied) return true;
  return isMarketingAllowed();
}

interface StoredUserData extends Partial<Record<PiiField, string>> {
  ts: number;
}

/**
 * All hashed variant keys from the pii-capture contract, plus the profile
 * fields from setUserData. em_g/em_r/em_s and ph_e are the per-platform email
 * and phone variants; they must survive the form-page to thank-you-page
 * redirect together with the base hashes.
 */
const PII_FIELDS = [
  "em", "em_g", "em_r", "em_s",
  "ph", "ph_e",
  "fn", "ln",
  "ct", "st", "zp", "country", "ge", "db",
  // Plaintext names, only present with the LinkedIn opt-in toggle
  "li_fn", "li_ln",
  // The customer's own account id, hashed. Outlives the anchor cookie, which
  // is the whole reason it is worth carrying across a redirect.
  "user_id",
] as const;
type PiiField = (typeof PII_FIELDS)[number];

/** Save current PII hashes from state.userData to localStorage. */
export function persistUserData(): void {
  if (!isStorageAllowed()) return;
  try {
    const data: StoredUserData = { ts: Date.now() };

    for (const key of PII_FIELDS) {
      if (state.userData[key]) {
        data[key] = state.userData[key];
      }
    }

    // Only persist if we have at least one field
    if (!PII_FIELDS.some((key) => data[key])) return;

    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    debugLog("userDataStore", "persisted", PII_FIELDS.filter((k) => data[k]).join(","));
  } catch {
    // localStorage unavailable (private browsing, quota exceeded, etc.)
  }
}

/**
 * Load persisted email/phone hashes into state.userData.
 * Skips fields that are already populated (fresh capture takes priority).
 * Clears expired data.
 */
export function loadPersistedUserData(): void {
  if (!isStorageAllowed()) return;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;

    const data: StoredUserData = JSON.parse(raw);

    // Check TTL
    if (Date.now() - data.ts > TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      debugLog("userDataStore", "expired, cleared");
      return;
    }

    let restored = false;

    for (const key of PII_FIELDS) {
      if (data[key] && !state.userData[key]) {
        state.userData[key] = data[key]!;
        restored = true;
      }
    }

    if (restored) {
      debugLog("userDataStore", "restored", PII_FIELDS.filter((k) => data[k]).join(","));
    }
  } catch {
    // localStorage unavailable or corrupted data
  }
}
